import CoreUtils from '../../../utils/coreUtils.js';
import Spline from '../spline.js';
import Node from '../node.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';
import Utils2D from '../../../utils/utils2D.js';
import Utils3D from '../../utils/utils3D.js';
import InputConnector from '../inputConnector.js';
import ShortestPathFinder from '../../shortestPathFinder';
import { CSG } from '../csg2d.js';

const CREATE_SEPARATE_MESHES = true;

const DEBUG_VISUALIZE_PLANES = false;
const DEBUG_VISUALIZE_RAYS = false;
const DEBUG_VISUALIZE_EDGES = false;

const DEBUG_PLANE_SIZE = 4;
const DEBUG_RAY_SCALE = 8;
const DEBUG_RAY_ORIGIN_OFFSET = -4;

export default class NodeCreateRoof extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'geo', null);

		this.addInput(PARAM_TYPE.FLOAT, 'slope', 0.5, { minValue: 0.01, maxValue: 2, units: PARAM_UNITS.NONE });
		// this.addInput(PARAM_TYPE.FLOAT, 'overhang', 0, { minValue: 0.01, maxValue: 2, units: PARAM_UNITS.METERS });

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');

		this.lastNumInputPoints = -1;
	}
	
	getType()
	{
		return 'createRoof';
	}

	getRefreshEnableStatusAfterParamChange(strInputName)
	{
		return strInputName == 'geo' ;
	}

	regeneratePerPointInputs()
	{
		let pSpline = new Spline(this.getLastInputResult('geo'));

		if(this.lastNumInputPoints === pSpline.getNumPoints())
			return;

		let pInput;
		let pNewInputs = [this.getInput('geo'), this.getInput('slope')];
		for(let idx = 0; idx < pSpline.getNumPoints(); idx++)
		{
			pInput = new InputConnector(PARAM_TYPE.FLOAT, `seg${idx + 1}_slope`, null, -1, { minValue: -1, maxValue: 2, units: PARAM_UNITS.NONE });
			pNewInputs.push(pInput);
		}
		this.updateInputsOutputs(pNewInputs);
		this.lastNumInputPoints = pSpline.getNumPoints();
	}

	convertSurfacesToMeshes(scene, surfaces, upVec)
	{
		let vertexData = new BABYLON.VertexData();
		vertexData.positions = [];
		vertexData.indices = [];
		let idx = 0;
		let res = [];
		for(let surfacePoints of surfaces)
		{
			// Otherwise, earcut:
			const indexOffset = vertexData.positions.length/3;

			let polygon = BABYLON.Mesh.CreatePolygon('temp', surfacePoints, scene);
			let triangulatedIndices = polygon.getIndices();
			// This may not work long term because it assumes that internally, BJS reuses
			// our point list and doesn't change it in any way...
			let processedPoints = surfacePoints;
			const indices = triangulatedIndices;
			polygon.dispose();

			//const indices = [];
			//for(let triIdx = 2; triIdx < processedPoints.length; triIdx++)
			//	indices.push(0, triIdx - 1, triIdx);

			// Test the orientation of each triangle, since they may be inconsistent
			for(let triIdx = 0; triIdx < indices.length; triIdx += 3)
			{
				const tempSpline = new Spline([processedPoints[indices[triIdx]], processedPoints[indices[triIdx + 1]], processedPoints[indices[triIdx + 2]]], true);

				// Handle cases where we're aligned with the up axis as is the case for vertical faces:
				let testDir = upVec;
				if(Math.abs(BABYLON.Vector3.Dot(tempSpline.getNormal(), upVec)) < 1e-3)
					testDir = null;
				if(!tempSpline.getIsClockwise(testDir))
				{
					const temp = indices[triIdx];
					indices[triIdx] = indices[triIdx + 2];
					indices[triIdx + 2] = temp;
				}
			}

			for(let triIdx = 0; triIdx < indices.length; triIdx++)
				indices[triIdx] += indexOffset;

			const currPositions = processedPoints.flatMap(vert => [vert.x, vert.y, vert.z]);
			vertexData.positions = vertexData.positions.concat(currPositions);
			vertexData.indices = vertexData.indices.concat(indices);

			if(CREATE_SEPARATE_MESHES)
			{
				let mesh = new BABYLON.Mesh(`${this.getGeneratedMeshName()}-${idx}`, scene);
				vertexData.applyToMesh(mesh);
				mesh.createNormals();
				res.push(mesh);
				this.onMeshCreated(mesh);

				vertexData.positions = [];
				vertexData.indices = [];
			}
			idx++;
		}	

		if(!CREATE_SEPARATE_MESHES)
		{
			let mesh = new BABYLON.Mesh(this.getGeneratedMeshName(), scene);
			vertexData.applyToMesh(mesh);
			mesh.createNormals();
			res.push(mesh);
			this.onMeshCreated(mesh);
		}

		return res;
	}

	getSurfaceNormal(points)
	{
		// Use the first two points and find a third that creates a valid
		// triangular area. Note that this fails if the points 0 and 1
		// are identical...
		for(let idx = 2; idx < points.length; idx++)
		{
			const dir1 = points[idx].subtract(points[0]);
			const dir2 = points[1].subtract(points[0]);
			dir1.normalize();
			dir2.normalize();
			const normal = BABYLON.Vector3.Cross(dir1, dir2);
			if(normal.length() > 1e-4)
			{
				normal.normalize();
				return normal;
			}
		}

		return new BABYLON.Vector3(0, 1, 0);
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{
		this.regeneratePerPointInputs();
		if(!inputValues['geo']?.points) 
			return null;

		const inputDataCopy = CoreUtils.clone(inputValues['geo']);
		for(let idx = 0; idx < inputDataCopy.points.length; idx++)
		{
			const perPointSlope = this.getInput(`seg${idx + 1}_slope`).getLastResult();
			if(perPointSlope >= 0)
				inputDataCopy.points[idx]['slope'] = perPointSlope;
		}

		let pSpline = new Spline(inputDataCopy);
		if(pSpline.getNumPoints() === 0)
			pSpline = Spline.createRectangle(3, 2);

		const upVec = pSpline.getNormal();
		if(BABYLON.Vector3.Dot(BABYLON.Vector3.Up(), upVec) < 0)
			upVec.scaleInPlace(-1);

		if(!pSpline.getIs3D())
			pSpline.unprojectOntoPlane(upVec);

		const VERTEX_EPSILON = 0.01;
		const VERTEX_SNAP_EPSILON = 0.05;

		pSpline.ensureOrientation();
		pSpline.removeCollinearPoints();
		pSpline.removeDuplicatePoints(VERTEX_SNAP_EPSILON);

		const bbox = pSpline.getBBox();
		const defaultSlope = inputValues['slope'];
		const planes = [];

		// Create planes for each edge
		const THIRD_POINT_SCALE = 0.01;
		const iNumPoints = pSpline.getNumPoints();
		for(let iPoint = 0; iPoint < iNumPoints; iPoint++)
		{
			const iNext = (iPoint + 1)%iNumPoints;
			const startPointInfo = pSpline.getPoint(iPoint);
			const vecStart = startPointInfo.point;
			const vecEnd = pSpline.getPoint(iNext).point;

			const dir = vecEnd.subtract(vecStart).normalize();
			const normal = BABYLON.Vector3.Cross(upVec, dir);
			normal.normalize();

			const currSlope = startPointInfo?.slope >= 0 ? startPointInfo?.slope : defaultSlope; 

			const vecMiddle = vecStart.add(vecEnd).scale(0.5);  
			let vecThird;
			if(Math.abs(currSlope) < 1e-6)
				vecThird = vecMiddle.add(upVec.scale(THIRD_POINT_SCALE));
			else
				vecThird = vecMiddle.add(upVec.scale(currSlope*THIRD_POINT_SCALE)).add(normal.scale(-THIRD_POINT_SCALE));
			const plane = BABYLON.Plane.FromPoints(vecStart, vecEnd, vecThird);
			plane.normal.normalize();
			plane.normal.scaleInPlace(-1);
			plane.d *= -1;
			planes.push(plane);
			if(DEBUG_VISUALIZE_PLANES)
			{
				const dbVisPlane = Utils3D.debugVisualizePlane(plane, scene, DEBUG_PLANE_SIZE);
				dbVisPlane.name = `dbVisPlane${iPoint}`;
				this.onMeshCreated(dbVisPlane);
				dbVisPlane.setEnabled(true);
			}
		}

		let visLines = [];

		// Intersect all planes with each other to create rays
		const rays = [];
		for(let iPlane = 0; iPlane < planes.length; iPlane++)
		{
			for(let iPlaneInner = 0; iPlaneInner < planes.length; iPlaneInner++)
			{
				if(iPlane === iPlaneInner)
					continue;

				const intersectRay = Utils3D.getPlanePlaneIntersection(planes[iPlane], planes[iPlaneInner]);
				if(!intersectRay)
					continue;

				rays.push(intersectRay);
				intersectRay.name = `ray${iPlane}x${iPlaneInner}`;
				if(DEBUG_VISUALIZE_RAYS)
				{
					const vecRayEnd = intersectRay.origin.add(intersectRay.direction.scale(DEBUG_RAY_SCALE));
					const vecRayStart = intersectRay.origin.add(intersectRay.direction.scale(DEBUG_RAY_ORIGIN_OFFSET));
					visLines.push([vecRayStart, vecRayEnd]);

					let box = Utils3D.createDebugMarker(intersectRay.origin.clone(), 0.05, BABYLON.Color3.Red());
					this.onMeshCreated(box);
					box.setEnabled(true);
					box.name = intersectRay.name;

					box = Utils3D.createDebugMarker(vecRayEnd.clone(), 0.035, BABYLON.Color3.Red());
					this.onMeshCreated(box);
					box.setEnabled(true);
					box.name = intersectRay.name;

				}
			}
		}

		// Now, intersect rays with each other
		const groundPlane = new BABYLON.Plane.FromPositionAndNormal(bbox.minimumWorld, BABYLON.Vector3.Up());
		const pathInfos = [];
		const splitSpline = pSpline.clone();
		for(let iRay = 0; iRay < rays.length; iRay++)
		{
			const pRay = rays[iRay];

			const groundPlaneT = pRay.intersectsPlane(groundPlane);
			let minT = -Number.MAX_VALUE;
			let maxT = Number.MAX_VALUE;
			if(groundPlaneT !== null)
			{
				if(pRay.origin.y < bbox.minimumWorld.y)
					minT = groundPlaneT;
				else if(pRay.origin.y > bbox.minimumWorld.y)
					maxT = groundPlaneT;
			}

			// If the ray is looking away from the ground plane, we need to check another direction.
			const pFlippedRay = new BABYLON.Ray(pRay.origin, pRay.direction.scale(-1));
			const groundPlaneTFlipped = pFlippedRay.intersectsPlane(groundPlane);
			if(groundPlaneTFlipped !== null)
			{
				// Notice we flip min/max here because we flipped the direction
				if(pRay.origin.y < bbox.minimumWorld.y)
					maxT = -groundPlaneTFlipped;
				else if(pRay.origin.y > bbox.minimumWorld.y)
					minT = -groundPlaneTFlipped;
			}

			const tValues = [];
			for(let iRayInner = 0; iRayInner < rays.length; iRayInner++)
			{
				if(iRay === iRayInner)
					continue;

				const info = Utils3D.getRayRayIntersection(pRay, rays[iRayInner]);
				if(!info.point)
					continue;

				const finalT = CoreUtils.clamp(info.t1, minT, maxT);
				if(tValues.findIndex(value => Math.abs(value - finalT) < 1e-6) < 0)
					tValues.push(finalT);
			}

			// Also intersect with original edges. Note that we need
			// to split the original input edge if the point is in
			// the middle.
			for(let iPoint = 0; iPoint < iNumPoints; iPoint++)
			{
				const iNext = (iPoint + 1)%iNumPoints;
				const vecStart = pSpline.getPoint(iPoint).point;
				const vecEnd = pSpline.getPoint(iNext).point;
				const dir = vecEnd.subtract(vecStart);
				dir.normalize();

				const tempRay = new BABYLON.Ray(vecStart, dir);
				const info = Utils3D.getRayRayIntersection(pRay, tempRay);
				if(!info.point)
					continue;

				const finalT = CoreUtils.clamp(info.t1, minT, maxT);
				if(tValues.findIndex(value => Math.abs(value - finalT) < 1e-6) < 0)
					tValues.push(finalT);
			}

			tValues.sort((a, b) => { return a - b; });

			const funcDoesMatchExistingSegment = (point1, point2) => {
				for(const pathInfo of pathInfos)
				{
					if(Utils3D.areSegmentsIdentical(point1, point2, pathInfo.points[0], pathInfo.points[1], VERTEX_EPSILON)) {
						return true;
					}
				}
				return false;
			};

			const funcSnapToExistingPoint = (pointIn) => {
				for(const pathInfo of pathInfos)
				{
					if(pathInfo.points[0].subtract(pointIn).length() <= VERTEX_SNAP_EPSILON)
						return pathInfo.points[0].clone();
					if(pathInfo.points[1].subtract(pointIn).length() <= VERTEX_SNAP_EPSILON)
						return pathInfo.points[1].clone();
				}
				return pointIn;
			};

			for(let index = 0; index < tValues.length - 1; index++)
			{
				let point1 = pRay.origin.add(pRay.direction.scale(tValues[index]));
				let point2 = pRay.origin.add(pRay.direction.scale(tValues[index + 1]));

				if(point2.subtract(point1).length() <= VERTEX_EPSILON)
					continue;

				// TODO: This assumes a fixed Y-up
				if(point1.y < bbox.minimumWorld.y - 1e-4 || point2.y < bbox.minimumWorld.y - 1e-4)
					continue;

				point1 = funcSnapToExistingPoint(point1);
				point2 = funcSnapToExistingPoint(point2);

				if(point2.subtract(point1).length() <= VERTEX_SNAP_EPSILON)
					continue;

				if(funcDoesMatchExistingSegment(point1, point2))
					continue;

				// See if any newly created points lie on boundary of the polygon
				for(let testPoint of [point1, point2])
				{
					let closeInfo = splitSpline.getPointClosestTo(testPoint.x, testPoint.y, testPoint.z);
					if(closeInfo.isOnSegment && closeInfo.normalDist < VERTEX_EPSILON)
					{
						splitSpline.splitAtNearestTo(testPoint.x, testPoint.y, testPoint.z, VERTEX_EPSILON);
					}
				}

				pathInfos.push({ id: pathInfos.length + 1, points: [point1, point2]});

				if(DEBUG_VISUALIZE_EDGES)
				{
					// CoreUtils.log(`${iRay}-${index} - ${point1.asArray()} to ${point2.asArray()}`);
					let box = Utils3D.createDebugMarker(point1, 0.015);
					box.name = `debEdge-${iRay}-${index}-start`;
					this.onMeshCreated(box);
					box.setEnabled(true);
					box = Utils3D.createDebugMarker(point2, 0.025);
					box.name = `debEdge-${iRay}-${index}-end`;
					this.onMeshCreated(box);
					box.setEnabled(true);
				}
			}
		}

		if(DEBUG_VISUALIZE_RAYS)
		{
			const dbEdges = BABYLON.MeshBuilder.CreateLineSystem('lines', { lines: visLines }, scene);
			this.onMeshCreated(dbEdges);
			dbEdges.setEnabled(true);
		}

		if(DEBUG_VISUALIZE_EDGES)
		{
			const dbEdges = BABYLON.MeshBuilder.CreateLineSystem('lines', { lines: pathInfos.map(info => info.points.slice()) }, scene);
			this.onMeshCreated(dbEdges);
			dbEdges.setEnabled(true);
		}

		const pathIdsSet = {};
		let surfaces = [];
		const iNumSplitPoints = splitSpline.getNumPoints();
		for(let iPoint = 0; iPoint < iNumSplitPoints; iPoint++)
		{
			const iNext = (iPoint + 1)%iNumSplitPoints;
			const vecStart = splitSpline.getPoint(iPoint).point;
			const vecEnd = splitSpline.getPoint(iNext).point;

			const shortestPathData = ShortestPathFinder.findShortestPath3D(vecStart, vecEnd, pathInfos, null, VERTEX_EPSILON);
			const surfacePoints = shortestPathData.points;
			if(!surfacePoints) {
				// const shortestPathData2 = ShortestPathFinder.findShortestPath3D(vecStart, vecEnd, pathInfos, null, VERTEX_EPSILON);
				continue;
			}

			shortestPathData.idsUsed.forEach(id => {
				if(pathIdsSet[id])
					pathIdsSet[id]++;
				else
					pathIdsSet[id] = 1;
			});

			surfaces.push(surfacePoints);
		}

		// For closing the remaining gaps, we need to assign weights, and make those
		// edges that are not part of the roof already weigh a lot more. Otherwise, the
		// shortest path might be one where a piece of the surface sticks out, making for
		// a hole-ly overall roof.
		for(const pathInfo of pathInfos)
		{
			if(pathIdsSet[pathInfo.id] === undefined)
				pathInfo.weight = 10;
		}
		
		// Now look for any edges that have not been used twice yet
		const origUsedIds = Object.assign({}, pathIdsSet);
		const candidateFillerSurfaces = [];
		for(const pathInfo of pathInfos)
		{
			if(pathIdsSet[pathInfo.id] != 1)
				continue;

			const shortestPathData = ShortestPathFinder.findShortestPath3D(pathInfo.points[0], pathInfo.points[1], pathInfos, new Set([pathInfo.id]), VERTEX_EPSILON);
			const surfacePoints = shortestPathData.points;
			if(!surfacePoints)
				continue;

			// We need to make sure these aren't used again:
			// Note that we explicitly set them to be unusable
			// in further iterations of this loop.
			shortestPathData.idsUsed.forEach(id => {
				pathIdsSet[id] = 2;

				const currPath = pathInfos.filter(currInfo => currInfo.id == id)[0];
				currPath.weight = 1;

				if(origUsedIds[id])
					origUsedIds[id]++;
				else
					origUsedIds[id] = 1;
			});

			candidateFillerSurfaces.push({surfacePoints, ids: shortestPathData.idsUsed});
		}

		// We only add those candidate surfaces whose sides are used in more than one polygon:
		for(const candidateInfo of candidateFillerSurfaces)
		{
			const numOpenSides = candidateInfo.ids.filter(id => origUsedIds[id] !== 2).length;
			if(numOpenSides == 0)
				surfaces.push(candidateInfo.surfacePoints);
		}

		// Merge coplanar sets of surfaces that touch each other. Just do iteratvely for simplicity.
		let bDidMerge;
		do
		{
			// Find two surfaces to merge
			bDidMerge = false;
			for(let iSurface1 = surfaces.length - 1; !bDidMerge && iSurface1 >= 0; iSurface1--)
			{
				const surf1 = surfaces[iSurface1];
				const normal1 = this.getSurfaceNormal(surf1);
				for(let iSurface2 = 0; !bDidMerge && iSurface2 < surfaces.length; iSurface2++)
				{
					if(iSurface1 === iSurface2)
						continue;

					const surf2 = surfaces[iSurface2];
					const normal2 = this.getSurfaceNormal(surf2);

					if(Math.abs(BABYLON.Vector3.Dot(normal1, normal2)) < 0.999)
						continue;

					// Otherwise, test if they're touching
					let bDoTouch = false;
					for(let iPoint1 = 0; !bDoTouch && iPoint1 < surf1.length; iPoint1++)
					{
						const iNext1 = (iPoint1 + 1) % surf1.length;
						for(let iPoint2 = 0; !bDoTouch && iPoint2 < surf2.length; iPoint2++)
						{
							const iNext2 = (iPoint2 + 1) % surf2.length;
							if(Utils3D.doesSegmentContain(surf1[iPoint1], surf1[iNext1], surf2[iPoint2], surf2[iNext2])
								|| Utils3D.doesSegmentContain(surf2[iPoint2], surf2[iNext2], surf1[iPoint1], surf1[iNext1]))
							{
								bDoTouch = true;
								break;
							}
						}
					}

					if(!bDoTouch)
						continue;

					// If they are, merge:
					const spline1 = new Spline(surf1.map(v => v.clone()));
					spline1.ensureOrientation(true, normal1);
					spline1.projectOntoPlane(normal1);

					// Store the distance to that plane along the normal
					const planeDist = BABYLON.Vector3.Dot(normal1, surf1[0]);

					// Note that we deliberately use normal1 for consistency in case they're
					// facing in different directions
					const spline2 = new Spline(surf2.map(v => v.clone()));
					spline2.ensureOrientation(true, normal1);
					spline2.projectOntoPlane(normal1);

					let subjectPolygons = CSG.fromPolygons([spline1.getLinearPoints()]);
					let currPolygons = CSG.fromPolygons([spline2.getLinearPoints()]);

					subjectPolygons = subjectPolygons.union(currPolygons).toPolygons();

					// We should only get 1 polygon - if we get two, something is wrong
					if(subjectPolygons.length != 1)
					{
						console.error(`Error merging coplanar roof surfaces`);
						continue;
					}

					let pTempSpline = new Spline();
					pTempSpline.setFromLinearPoints(subjectPolygons[0], true);
					pTempSpline.removeDuplicatePoints();
					pTempSpline.removeCollinearPoints();

					pTempSpline.unprojectOntoPlane(normal1);
					const moveBackVec = normal1.scale(planeDist);
					pTempSpline.translate3D(moveBackVec.x, moveBackVec.y, moveBackVec.z);

					// Finally, delete this surface and update surface 1 points:
					surfaces[iSurface1] = pTempSpline.getLinearPoints();
					surfaces.splice(iSurface2, 1);
					bDidMerge = true;
					break;
				}
			}
		}
		while(bDidMerge);
		
		return this.convertSurfacesToMeshes(scene, surfaces, upVec);
	}
}
